/**
 * Domain Modal - Modal para crear/editar dominios
 */
import { useState, useEffect } from 'react'
import { Button } from '@trafiflow/shared-ui'
import apiClient from '../../../shared/lib/api'
import type { Domain, DomainType, Brand } from '../types'

interface DomainModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  domain: Domain | null
  type: DomainType
  brands?: Brand[]
}

export default function DomainModal({
  isOpen,
  onClose,
  onSave,
  domain,
  type,
  brands = [],
}: DomainModalProps) {
  const [domainValue, setDomainValue] = useState('')
  const [brandId, setBrandId] = useState('')
  const [facebookPageId, setFacebookPageId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setDomainValue(domain?.domain || '')
      setBrandId(domain?.brandId || '')
      setFacebookPageId(domain?.facebookPageId || '')
      setError(null)
    }
  }, [isOpen, domain])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!domainValue.trim()) {
      setError('Domain is required')
      return
    }

    // Validar formato básico de dominio
    const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i
    if (!domainRegex.test(domainValue.trim())) {
      setError('Please enter a valid domain (e.g., example.com)')
      return
    }

    // Validar que brandId sea requerido para competitors y benchmarks
    if ((type === 'competitor' || type === 'benchmark') && !brandId) {
      setError(`Brand is required for ${type === 'competitor' ? 'competitors' : 'benchmarks'}`)
      return
    }

    try {
      setLoading(true)

      if (domain) {
        // Actualizar
        const updateData: { domain: string; brandId?: string; facebookPageId?: string } = {
          domain: domainValue.trim(),
        }
        if (brandId) {
          updateData.brandId = brandId
        }
        if (facebookPageId.trim()) {
          updateData.facebookPageId = facebookPageId.trim()
        }
        await apiClient.put(`/my-company/domains/${domain.id}`, updateData)
      } else {
        // Crear
        const createData: { domain: string; type: DomainType; brandId?: string; facebookPageId?: string } = {
          domain: domainValue.trim(),
          type,
        }
        if (brandId) {
          createData.brandId = brandId
        }
        if (facebookPageId.trim()) {
          createData.facebookPageId = facebookPageId.trim()
        }
        await apiClient.post('/my-company/domains', createData)
      }

      onSave()
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string; error?: string } | string }; message?: string }
      console.error('Error saving domain:', err)
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data,
      })
      
      // Extract error message
      let errorMessage = 'Failed to save domain'
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error
        }
      } else if (err.message) {
        errorMessage = err.message
      }
      
      // If 404, the endpoint might not be deployed yet
      if (err.response?.status === 404) {
        errorMessage = 'Domain endpoint not found. Please ensure the API is deployed.'
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const typeLabels = {
    domain: 'Domain',
    competitor: 'Competitor',
    benchmark: 'Benchmark',
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-tf-bg-card rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-tf-text mb-4">
            {domain ? `Edit ${typeLabels[type]}` : `Add ${typeLabels[type]}`}
          </h2>

          <form onSubmit={handleSubmit}>
            {(type === 'competitor' || type === 'benchmark') && !domain && brands.length > 0 && (
              <div className="mb-4">
                <label htmlFor="brandId" className="block text-sm font-medium text-tf-text-sub mb-2">
                  Brand *
                </label>
                <select
                  id="brandId"
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                  className="w-full px-3 py-2 border border-tf-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  disabled={loading}
                  required
                >
                  <option value="">Select a brand</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="domain" className="block text-sm font-medium text-tf-text-sub mb-2">
                Domain
              </label>
              <input
                type="text"
                id="domain"
                value={domainValue}
                onChange={(e) => setDomainValue(e.target.value)}
                placeholder="example.com"
                className="w-full px-3 py-2 border border-tf-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                disabled={loading}
              />
            </div>

            {type === 'competitor' && (
              <div className="mb-4">
                <label htmlFor="facebookPageId" className="block text-sm font-medium text-tf-text-sub mb-2">
                  Facebook Page ID
                  <span className="text-tf-text-muted font-normal ml-1">(optional)</span>
                </label>
                <input
                  type="text"
                  id="facebookPageId"
                  value={facebookPageId}
                  onChange={(e) => setFacebookPageId(e.target.value)}
                  placeholder="e.g., 123456789012345"
                  className="w-full px-3 py-2 border border-tf-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-tf-text-muted">
                  Leave empty to auto-detect from the competitor's website
                </p>
              </div>
            )}

            {error && (
              <div className="mb-4 bg-tf-feedback-error-bg border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : domain ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

